import { Shield } from "lucide-react";
import { PageHeading, Section, Field, Steps, Callout, RoleBadge, Diagram, MockSelect, MockField, MockButton } from "../shared";

export default function AdminTransitionPermissionsSection() {
  return (
    <div data-testid="guide-admin-transition-permissions">
      <PageHeading
        icon={Shield}
        title="Transition Permissions"
        description="A way to lock down a status. For each target status you can list exactly who is allowed to move items into it. If a status has no list, it stays open to all administrators."
      />

      <Callout variant="info" title="In plain words">
        This screen answers "who is allowed to push an item into this status?" Pick a destination status — say <span className="text-white">Signed Off</span> — and name the people allowed to send items there. The moment you name even one person, everyone else loses that ability.
      </Callout>

      <Section title="Who can manage it">
        <div className="flex items-center gap-2"><RoleBadge role="super_admin" /><span className="text-xs text-white/55"><span className="text-white">manage_governance</span> on a draft version. Stored in the <span className="text-white">v2_transition_permissions</span> table.</span></div>
      </Section>

      <Section title="When do I use this?">
        <p className="text-sm text-white/65 leading-relaxed">
          Use it when a sensitive status (like Signed Off or Closed) should only be reachable by named people, not by any admin. Add one row per allowed person and activate the draft.
        </p>
      </Section>

      <Section title="Locking down a destination">
        <Diagram
          frameLabel="Admin → Transition Permissions (on a draft version)"
          legend={[
            <>Pick the <span className="text-white">target status</span> you want to guard.</>,
            <>Add the <span className="text-white">name</span> of each allowed Team Member (must match exactly).</>,
            <>Leave <span className="text-white">Can perform</span> on to allow, then Save and activate the draft.</>,
          ]}
        >
          <MockSelect label="To status" value="SIGNED_OFF" marker={1} />
          <MockField label="Person name" value="SDF MD" marker={2} />
          <MockField label="Can perform" value="Yes" marker={3} />
          <MockButton label="Save" />
        </Diagram>
        <Steps steps={[
          <>Pick the target status, e.g. <span className="text-white">SIGNED_OFF</span>.</>,
          <>Add one row per allowed person. As soon as the first row exists for that destination, every other admin loses the ability to use it.</>,
          <>Activate the draft from Versions.</>,
        ]} />
      </Section>

      <Section title="Rule fields">
        <div className="space-y-2">
          <Field name="To status" required="required"><p>The destination status this rule guards (e.g. <span className="text-white">SIGNED_OFF</span>, <span className="text-white">CLOSED</span>).</p></Field>
          <Field name="Person name" required="required"><p>Display name of a Team Member who is allowed to move items into this status. Must match an existing Team Member name exactly.</p></Field>
          <Field name="Can perform" required="required"><p>Toggle on to allow, off to record an explicit deny (rarely useful — leaving the row off entirely has the same effect when other rules exist).</p></Field>
        </div>
      </Section>

      <Callout variant="warning" title="Empty list = open">
        Add at least one row per destination you want to lock. A destination with zero rules is open to every admin who would otherwise be allowed by the workflow.
      </Callout>
      <Callout variant="info" title="Different from Field Permissions">
        Field Permissions gate <em>which fields</em> a role can edit. Transition Permissions gate <em>which people</em> can drive an item into a specific status. The two layer together.
      </Callout>
    </div>
  );
}
